/**
 * FITNESS SOCIETY SUPPLEMENTS — PREMIUM SHOPIFY THEME
 * theme.js — Vanilla JS, no dependencies
 * v1.0
 */

'use strict';

/* ==========================================================================
   UTILITIES
   ========================================================================== */

/**
 * Run a function after the DOM is ready.
 * Calls immediately if the DOM is already parsed.
 */
function onReady(fn) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn);
  } else {
    fn();
  }
}

/**
 * Debounce a function call.
 * @param {Function} fn
 * @param {number} wait
 * @returns {Function}
 */
function debounce(fn, wait) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Safely query a single element.
 * @param {string} selector
 * @param {Element|Document} context
 * @returns {Element|null}
 */
function qs(selector, context) {
  return (context || document).querySelector(selector);
}

/**
 * Safely query all elements as an array.
 * @param {string} selector
 * @param {Element|Document} context
 * @returns {Element[]}
 */
function qsa(selector, context) {
  return Array.from((context || document).querySelectorAll(selector));
}


/* ==========================================================================
   1. SCROLL HANDLER — sticky header
   ========================================================================== */

function initScrollHandler() {
  const header = qs('.site-header');
  if (!header) return;

  let ticking = false;
  const SCROLL_THRESHOLD = 20;

  function updateHeader() {
    const scrolled = window.scrollY > SCROLL_THRESHOLD;
    header.classList.toggle('is-scrolled', scrolled);
    ticking = false;
  }

  window.addEventListener('scroll', function () {
    if (!ticking) {
      requestAnimationFrame(updateHeader);
      ticking = true;
    }
  }, { passive: true });

  // Run on init
  updateHeader();
}


/* ==========================================================================
   2. HEADER HEIGHT — CSS custom property
   ========================================================================== */

function initHeaderHeight() {
  const header = qs('.site-header');
  if (!header) return;

  function setHeaderHeight() {
    const height = header.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--header-height', height + 'px');
  }

  setHeaderHeight();

  // Update on resize (debounced)
  window.addEventListener('resize', debounce(setHeaderHeight, 150), { passive: true });
}


/* ==========================================================================
   3. MOBILE MENU
   ========================================================================== */

function initMobileMenu() {
  const menu       = qs('.mobile-menu');
  const openBtn    = qs('.hamburger-btn');
  const closeBtn   = qs('.mobile-menu__close');

  if (!menu || !openBtn) return;

  let isOpen = false;

  function openMenu() {
    isOpen = true;
    menu.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    openBtn.setAttribute('aria-expanded', 'true');
    menu.setAttribute('aria-hidden', 'false');

    // Focus the close button for accessibility
    if (closeBtn) {
      setTimeout(function () { closeBtn.focus(); }, 50);
    }
  }

  function closeMenu() {
    isOpen = false;
    menu.classList.remove('is-open');
    document.body.style.overflow = '';
    openBtn.setAttribute('aria-expanded', 'false');
    menu.setAttribute('aria-hidden', 'true');
    openBtn.focus();
  }

  openBtn.addEventListener('click', function () {
    isOpen ? closeMenu() : openMenu();
  });

  if (closeBtn) {
    closeBtn.addEventListener('click', closeMenu);
  }

  // Close on Escape
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen) {
      closeMenu();
    }
  });

  // Close when clicking a nav link (single-page navigation)
  qsa('.mobile-menu__nav-link', menu).forEach(function (link) {
    link.addEventListener('click', function () {
      // Small delay allows page to start loading
      setTimeout(closeMenu, 200);
    });
  });

  // Trap focus inside open menu
  menu.addEventListener('keydown', function (e) {
    if (!isOpen || e.key !== 'Tab') return;

    const focusable = qsa(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      menu
    );
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  // Initial ARIA state
  openBtn.setAttribute('aria-expanded', 'false');
  openBtn.setAttribute('aria-controls', menu.id || 'mobile-menu');
  menu.setAttribute('aria-hidden', 'true');
}


/* ==========================================================================
   4. ANNOUNCEMENT BAR
   ========================================================================== */

function initAnnouncementBar() {
  const bar      = qs('.announcement-bar');
  const closeBtn = qs('.announcement-bar__close');

  if (!bar || !closeBtn) return;

  const STORAGE_KEY = 'fs-announcement-dismissed';

  // If already dismissed this session, hide immediately
  if (sessionStorage.getItem(STORAGE_KEY) === 'true') {
    bar.style.display = 'none';
    return;
  }

  closeBtn.addEventListener('click', function () {
    bar.style.maxHeight = bar.scrollHeight + 'px';

    requestAnimationFrame(function () {
      bar.style.transition = 'max-height 250ms ease, opacity 250ms ease, padding 250ms ease';
      bar.style.maxHeight  = '0';
      bar.style.opacity    = '0';
      bar.style.padding    = '0';
      bar.style.overflow   = 'hidden';
    });

    setTimeout(function () {
      bar.style.display = 'none';
      sessionStorage.setItem(STORAGE_KEY, 'true');

      // Re-measure header height after bar is gone
      const resizeEvent = new Event('resize');
      window.dispatchEvent(resizeEvent);
    }, 260);
  });
}


/* ==========================================================================
   5. ACCORDION (FAQ)
   ========================================================================== */

function initAccordions() {
  const triggers = qsa('.accordion-trigger');

  triggers.forEach(function (trigger) {
    const item    = trigger.closest('.accordion-item');
    const content = item ? item.querySelector('.accordion-content') : null;

    if (!item || !content) return;

    // Set initial ARIA state
    const expanded = item.classList.contains('is-open');
    trigger.setAttribute('aria-expanded', String(expanded));

    const contentId = content.id || ('accordion-content-' + Math.random().toString(36).slice(2, 7));
    content.id = contentId;
    trigger.setAttribute('aria-controls', contentId);
    content.setAttribute('role', 'region');
    content.setAttribute('aria-labelledby', trigger.id || '');

    trigger.addEventListener('click', function () {
      const isOpen = item.classList.contains('is-open');

      // Optional: close all others (comment out for multi-open)
      qsa('.accordion-item.is-open').forEach(function (openItem) {
        if (openItem !== item) {
          openItem.classList.remove('is-open');
          const openTrigger = openItem.querySelector('.accordion-trigger');
          if (openTrigger) openTrigger.setAttribute('aria-expanded', 'false');
        }
      });

      item.classList.toggle('is-open', !isOpen);
      trigger.setAttribute('aria-expanded', String(!isOpen));
    });
  });
}


/* ==========================================================================
   6. SCROLL ANIMATIONS — IntersectionObserver
   ========================================================================== */

function initScrollAnimations() {
  const elements = qsa('.animate-on-scroll');
  if (!elements.length) return;

  // Fallback for browsers without IntersectionObserver
  if (!('IntersectionObserver' in window)) {
    elements.forEach(function (el) {
      el.classList.add('is-visible');
    });
    return;
  }

  const observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target); // Animate once
        }
      });
    },
    {
      threshold: 0.15,
      rootMargin: '0px 0px -40px 0px'
    }
  );

  elements.forEach(function (el) {
    observer.observe(el);
  });
}


/* ==========================================================================
   7. ACTIVE NAV LINKS
   ========================================================================== */

function initActiveNavLinks() {
  const currentPath = window.location.pathname;
  const navLinks    = qsa('.nav-link, .mobile-menu__nav-link');

  navLinks.forEach(function (link) {
    const href = link.getAttribute('href');
    if (!href) return;

    try {
      const linkPath = new URL(href, window.location.origin).pathname;

      // Exact match, or starts-with for collection/blog sub-pages
      const isActive =
        linkPath === currentPath ||
        (linkPath !== '/' && currentPath.startsWith(linkPath));

      if (isActive) {
        link.classList.add('is-active');
        link.setAttribute('aria-current', 'page');
      }
    } catch (e) {
      // Malformed URL — skip
    }
  });
}


/* ==========================================================================
   8. THEME TOGGLE (light / dark)
   ========================================================================== */

function initThemeToggle() {
  const toggleBtns = qsa('[data-theme-toggle]');
  if (!toggleBtns.length) return;

  const STORAGE_KEY = 'fs-color-scheme';
  const html        = document.documentElement;

  // Determine initial theme: localStorage > data-theme attr > default 'dark'
  function getStoredTheme() {
    return localStorage.getItem(STORAGE_KEY);
  }

  function applyTheme(theme) {
    html.setAttribute('data-theme', theme);
    localStorage.setItem(STORAGE_KEY, theme);

    toggleBtns.forEach(function (btn) {
      btn.setAttribute('aria-pressed', String(theme === 'light'));
      btn.setAttribute('aria-label', theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode');
    });
  }

  // Apply stored preference on load
  const stored = getStoredTheme();
  if (stored) {
    applyTheme(stored);
  }

  toggleBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      const current = html.getAttribute('data-theme') || 'dark';
      applyTheme(current === 'dark' ? 'light' : 'dark');
    });
  });
}


/* ==========================================================================
   9. TABLE OF CONTENTS — active link tracking
   ========================================================================== */

function initTocTracking() {
  const toc = qs('.toc');
  if (!toc) return;

  const tocLinks = qsa('.toc__link', toc);
  if (!tocLinks.length) return;

  const headings = tocLinks.map(function (link) {
    const href = link.getAttribute('href');
    if (!href || !href.startsWith('#')) return null;
    return document.getElementById(href.slice(1));
  }).filter(Boolean);

  if (!headings.length) return;

  const observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        const id   = entry.target.id;
        const link = qs('.toc__link[href="#' + id + '"]', toc);

        tocLinks.forEach(function (l) { l.classList.remove('is-active'); });
        if (link) link.classList.add('is-active');
      });
    },
    {
      rootMargin: '0px 0px -60% 0px',
      threshold: 0
    }
  );

  headings.forEach(function (h) { observer.observe(h); });
}


/* ==========================================================================
   10. CART COUNT — update badge
   ========================================================================== */

function initCartCount() {
  const badges = qsa('.header-icon-btn__count[data-cart-count]');
  if (!badges.length) return;

  function updateCount(count) {
    badges.forEach(function (badge) {
      badge.textContent = count > 99 ? '99+' : String(count);
      badge.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  // Listen for Shopify cart update events (from sections or custom events)
  document.addEventListener('cart:updated', function (e) {
    if (e.detail && typeof e.detail.count === 'number') {
      updateCount(e.detail.count);
    }
  });
}


/* ==========================================================================
   11. SEARCH DRAWER
   ========================================================================== */

function initSearchDrawer() {
  const openBtns  = qsa('[data-search-open]');
  const drawer    = qs('.search-drawer');
  const closeBtn  = qs('.search-drawer__close');
  const input     = qs('.search-drawer__input', drawer);

  if (!drawer || !openBtns.length) return;

  function openDrawer() {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    if (input) setTimeout(function () { input.focus(); }, 100);
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  openBtns.forEach(function (btn) {
    btn.addEventListener('click', openDrawer);
  });

  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  // Click outside to close
  drawer.addEventListener('click', function (e) {
    if (e.target === drawer) closeDrawer();
  });
}


/* ==========================================================================
   12. PRODUCT IMAGE GALLERY — thumbnail switching
   ========================================================================== */

function initProductGallery() {
  const galleries = qsa('.product-gallery');

  galleries.forEach(function (gallery) {
    const main      = qs('.product-gallery__main-image', gallery);
    const thumbBtns = qsa('.product-gallery__thumb-btn', gallery);

    if (!main || !thumbBtns.length) return;

    thumbBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        const src    = btn.getAttribute('data-src');
        const srcset = btn.getAttribute('data-srcset');
        const alt    = btn.getAttribute('data-alt');

        if (src) main.src = src;
        if (srcset) main.srcset = srcset;
        if (alt)   main.alt  = alt;

        thumbBtns.forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-selected', 'false');
        });

        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
      });
    });
  });
}


/* ==========================================================================
   13. QUANTITY SELECTOR
   ========================================================================== */

function initQuantitySelectors() {
  const wrappers = qsa('.quantity-selector');

  wrappers.forEach(function (wrapper) {
    const input   = qs('input[type="number"]', wrapper);
    const minusBtn = qs('[data-qty-minus]', wrapper);
    const plusBtn  = qs('[data-qty-plus]', wrapper);

    if (!input) return;

    function updateButtons() {
      const val = parseInt(input.value, 10);
      const min = parseInt(input.min || '1', 10);
      const max = parseInt(input.max || '999', 10);

      if (minusBtn) minusBtn.disabled = val <= min;
      if (plusBtn)  plusBtn.disabled  = val >= max;
    }

    if (minusBtn) {
      minusBtn.addEventListener('click', function () {
        const val = parseInt(input.value, 10);
        const min = parseInt(input.min || '1', 10);
        if (val > min) {
          input.value = val - 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          updateButtons();
        }
      });
    }

    if (plusBtn) {
      plusBtn.addEventListener('click', function () {
        const val = parseInt(input.value, 10);
        const max = parseInt(input.max || '999', 10);
        if (val < max) {
          input.value = val + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          updateButtons();
        }
      });
    }

    input.addEventListener('change', updateButtons);
    updateButtons();
  });
}


/* ==========================================================================
   14. VARIANT SELECTOR — update URL + price display
   ========================================================================== */

function initVariantSelector() {
  const forms = qsa('.product-form[data-section-id]');

  forms.forEach(function (form) {
    const selects = qsa('select[name="id"], select.variant-select', form);

    selects.forEach(function (select) {
      select.addEventListener('change', function () {
        const variantId = select.value;
        if (!variantId) return;

        // Update URL without reloading
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());

        // Dispatch custom event for other components (e.g. price, images)
        document.dispatchEvent(new CustomEvent('variant:changed', {
          bubbles: true,
          detail: { variantId: parseInt(variantId, 10), form }
        }));
      });
    });
  });
}


/* ==========================================================================
   15. TOAST NOTIFICATIONS
   ========================================================================== */

const Toast = {
  container: null,

  getContainer: function () {
    if (!Toast.container) {
      Toast.container = qs('.toast-container');
      if (!Toast.container) {
        Toast.container = document.createElement('div');
        Toast.container.className = 'toast-container';
        Toast.container.setAttribute('aria-live', 'polite');
        Toast.container.setAttribute('aria-atomic', 'false');
        document.body.appendChild(Toast.container);
      }
    }
    return Toast.container;
  },

  show: function (message, type, duration) {
    type     = type     || 'default';
    duration = duration || 3500;

    const container = Toast.getContainer();
    const toast     = document.createElement('div');

    toast.className  = 'toast toast--' + type;
    toast.setAttribute('role', 'status');
    toast.innerHTML  = '<span class="toast__message">' + message + '</span>';

    container.appendChild(toast);

    setTimeout(function () {
      toast.style.transition = 'opacity 250ms ease, transform 250ms ease';
      toast.style.opacity    = '0';
      toast.style.transform  = 'translateY(8px)';
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 260);
    }, duration);

    return toast;
  }
};

// Expose globally for use in sections
window.FsToast = Toast;


/* ==========================================================================
   16. LAZY IMAGE LOADING — native + fallback
   ========================================================================== */

function initLazyImages() {
  // Modern browsers handle loading="lazy" natively.
  // This polyfill handles older browsers or custom data-src patterns.
  const lazyImgs = qsa('img[data-src]');
  if (!lazyImgs.length) return;

  if ('IntersectionObserver' in window) {
    const imgObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        const img = entry.target;
        img.src   = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');
        imgObserver.unobserve(img);
      });
    }, { rootMargin: '200px' });

    lazyImgs.forEach(function (img) { imgObserver.observe(img); });
  } else {
    // Fallback: load all
    lazyImgs.forEach(function (img) {
      img.src = img.dataset.src;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    });
  }
}


/* ==========================================================================
   17. SMOOTH ANCHOR SCROLLING
   ========================================================================== */

function initSmoothAnchors() {
  document.addEventListener('click', function (e) {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;

    const href   = link.getAttribute('href');
    if (href === '#') return;

    const target = document.getElementById(href.slice(1));
    if (!target) return;

    e.preventDefault();

    const headerHeight = parseInt(
      getComputedStyle(document.documentElement)
        .getPropertyValue('--header-height') || '64',
      10
    );

    const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 24;

    window.scrollTo({ top, behavior: 'smooth' });

    // Update URL without scroll jump
    history.pushState(null, '', href);

    // Focus for accessibility
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });
}


/* ==========================================================================
   INIT — Bootstrap all modules
   ========================================================================== */

onReady(function () {
  initHeaderHeight();
  initScrollHandler();
  initMobileMenu();
  initAnnouncementBar();
  initAccordions();
  initScrollAnimations();
  initActiveNavLinks();
  initThemeToggle();
  initTocTracking();
  initCartCount();
  initSearchDrawer();
  initProductGallery();
  initQuantitySelectors();
  initVariantSelector();
  initLazyImages();
  initSmoothAnchors();
});

/* ==========================================================================
   SHOPIFY THEME EDITOR — Section event hooks
   Re-initialize JS when theme editor reloads or reorders a section
   ========================================================================== */

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', function (e) {
    var container = e.target;
    initHeaderHeight();
    initAccordions();
    initScrollAnimations();
    initProductGallery();
    initQuantitySelectors();
    initVariantSelector();
    initLazyImages();
    // Re-init announcement bar if that section loaded
    if (container.querySelector('[data-announcement-bar]')) {
      initAnnouncementBar();
    }
    // Re-init mobile menu if header loaded
    if (container.querySelector('[data-site-header]')) {
      initMobileMenu();
    }
  });

  document.addEventListener('shopify:section:unload', function () {
    initHeaderHeight();
  });

  document.addEventListener('shopify:section:reorder', function () {
    initScrollAnimations();
  });

  document.addEventListener('shopify:inspector:activate', function () {
    // Disable scroll animations in editor for better editing experience
    document.querySelectorAll('[data-animation]').forEach(function (el) {
      el.classList.add('animated');
    });
  });
}
